import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: "developpement_app", label: "Dév app", long: "Développement app" },
  { key: "compta", label: "Compta", long: "Compta" },
  { key: "finance", label: "Finance", long: "Finance" },
  { key: "gestion", label: "Gestion", long: "Gestion" },
  { key: "devis", label: "Devis", long: "Devis" },
  { key: "test_recette", label: "Recette", long: "Tests & recette" },
  { key: "serveur", label: "Serveur", long: "Serveur" },
  { key: "documents", label: "Docs", long: "Documents" },
];

const STATUTS = {
  lancement: {
    label: "Lancement",
    dot: "bg-white border border-black/15",
    bubble: "bg-white border-black/10 text-[#1a1a1a]",
    text: "text-black/45",
  },
  travaille: {
    label: "Travaille",
    dot: "bg-orange-400",
    bubble: "bg-orange-100 border-orange-200 text-orange-950",
    text: "text-orange-700",
  },
  termine: {
    label: "Terminé",
    dot: "bg-emerald-500",
    bubble: "bg-emerald-100 border-emerald-200 text-emerald-950",
    text: "text-emerald-700",
  },
  bloque: {
    label: "Bloqué",
    dot: "bg-red-500",
    bubble: "bg-red-100 border-red-200 text-red-950",
    text: "text-red-700",
  },
};

function useVisibleViewport() {
  const [v, setV] = useState(() => ({
    hauteur: typeof window !== "undefined" ? window.innerHeight : 0,
    decalage: 0,
  }));
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const maj = () => setV({ hauteur: vv.height, decalage: vv.offsetTop });
    vv.addEventListener("resize", maj);
    vv.addEventListener("scroll", maj);
    maj();
    return () => {
      vv.removeEventListener("resize", maj);
      vv.removeEventListener("scroll", maj);
    };
  }, []);
  return v;
}

function joliProjet(nom) {
  return (nom || "Projet")
    .replace(/novaforge|odoo|app|application/gi, "")
    .replace(/[-_]+/g, " ")
    .trim() || nom || "Projet";
}

function dateCourte(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function estInstallee() {
  if (typeof window === "undefined") return false;
  return window.navigator.standalone === true || window.matchMedia?.("(display-mode: standalone)")?.matches;
}

export default function MobileAgentCloudPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const token = localStorage.getItem("odo_studio_token");
  const vue = useVisibleViewport();

  const [projets, setProjets] = useState([]);
  const [taches, setTaches] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [categorie, setCategorie] = useState(params.get("cat") || "toutes");
  const [prompt, setPrompt] = useState("");
  const [creation, setCreation] = useState(false);
  const [selection, setSelection] = useState(null);
  const [erreur, setErreur] = useState("");
  const [installee, setInstallee] = useState(estInstallee);
  const [aideInstall, setAideInstall] = useState(() => localStorage.getItem("nova_agents_install_hint_closed") !== "1");

  const entetes = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const charger = async () => {
    if (!token) return;
    const [p, t] = await Promise.all([
      fetch(`${API_URL}/api/projects`, { headers: entetes }),
      fetch(`${API_URL}/api/agent-cloud/tasks`, { headers: entetes }),
    ]);
    if (p.ok) {
      const data = await p.json();
      data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setProjets(data);
      if (!projectId && data.length) setProjectId(data[0].id);
    }
    if (t.ok) setTaches(await t.json());
    setChargement(false);
  };

  useEffect(() => {
    charger();
    const intervalle = setInterval(charger, 5000);
    return () => clearInterval(intervalle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setInstallee(estInstallee());
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker.register("/agent-cloud-sw.js").catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const next = {};
    if (projectId) next.project = projectId;
    if (categorie !== "toutes") next.cat = categorie;
    setParams(next, { replace: true });
  }, [projectId, categorie, setParams]);

  const projetActif = projets.find((p) => p.id === projectId);
  const visibles = taches.filter((t) =>
    (!projectId || t.project_id === projectId) &&
    (categorie === "toutes" || t.category === categorie)
  );
  const stats = {
    lancement: visibles.filter((t) => t.status === "lancement").length,
    travaille: visibles.filter((t) => t.status === "travaille").length,
    termine: visibles.filter((t) => t.status === "termine").length,
    bloque: visibles.filter((t) => t.status === "bloque").length,
  };

  const creerTache = async () => {
    const texte = prompt.trim();
    if (!texte || !projectId || creation) return;
    setCreation(true);
    setErreur("");
    try {
      const r = await fetch(`${API_URL}/api/projects/${projectId}/agent-tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...entetes },
        body: JSON.stringify({
          prompt: texte,
          category: categorie === "toutes" ? "developpement_app" : categorie,
        }),
      });
      if (!r.ok) throw new Error("Création impossible");
      const tache = await r.json();
      setTaches((items) => [tache, ...items]);
      setSelection(tache);
      setPrompt("");
      setCategorie(tache.category);
      setTimeout(charger, 900);
    } catch (e) {
      setErreur(e.message || "Création impossible");
    } finally {
      setCreation(false);
    }
  };

  return (
    <div
      className="flex flex-col overflow-hidden bg-[#faf9f7] text-[#1a1a1a]"
      style={{
        position: "fixed",
        inset: 0,
        height: vue.hauteur ? `${vue.hauteur}px` : "100dvh",
        transform: `translateY(${vue.decalage}px)`,
      }}
    >
      <header
        className="shrink-0 border-b border-black/5 bg-[#faf9f7]/90 px-4 backdrop-blur"
        style={{ paddingTop: "calc(max(env(safe-area-inset-top), 44px) + 8px)", paddingBottom: "10px" }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/m")}
            className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full active:bg-black/5"
            aria-label="Retour"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[22px] font-semibold tracking-tight">Nuage agents</h1>
            <p className="truncate text-[12px] text-black/45">
              {projetActif ? joliProjet(projetActif.name) : "Choisir un projet"} · {visibles.length} tâche(s)
            </p>
          </div>
          <button
            onClick={charger}
            className="flex h-9 w-9 items-center justify-center rounded-full active:bg-black/5"
            aria-label="Rafraîchir"
          >
            ↻
          </button>
        </div>

        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {projets.map((p) => (
            <button
              key={p.id}
              onClick={() => setProjectId(p.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] font-medium ${
                p.id === projectId ? "bg-[#1a1a1a] text-white" : "bg-white text-black/55 border border-black/5"
              }`}
            >
              {joliProjet(p.name)}
            </button>
          ))}
        </div>

        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setCategorie("toutes")}
            className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-medium ${
              categorie === "toutes" ? "bg-blue-600 text-white" : "bg-white text-black/45 border border-black/5"
            }`}
          >
            Toutes
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategorie(c.key)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-medium ${
                categorie === c.key ? "bg-blue-600 text-white" : "bg-white text-black/45 border border-black/5"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-3 grid grid-cols-4 gap-1.5">
          {Object.entries(stats).map(([cle, nb]) => (
            <div key={cle} className="rounded-2xl bg-white px-2 py-2 text-center shadow-sm shadow-black/[0.02]">
              <div className={`mx-auto mb-1 h-2 w-2 rounded-full ${STATUTS[cle].dot}`} />
              <div className="text-[15px] font-semibold leading-none">{nb}</div>
              <div className="mt-0.5 text-[10px] text-black/35">{STATUTS[cle].label}</div>
            </div>
          ))}
        </div>

        {!installee && aideInstall && (
          <div className="mt-3 rounded-3xl border border-blue-100 bg-blue-50 px-3 py-3 text-[13px] text-blue-950">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">Installer comme app iPhone/iPad</div>
                <div className="mt-1 leading-relaxed text-blue-900/80">
                  Dans Safari : partager <span className="font-semibold">□↑</span> → “Ajouter à l’écran d’accueil”.
                  L’app ouvrira directement ce nuage.
                </div>
              </div>
              <button
                onClick={() => {
                  localStorage.setItem("nova_agents_install_hint_closed", "1");
                  setAideInstall(false);
                }}
                className="rounded-full bg-white/70 px-2 py-1 text-[12px] font-semibold text-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4" style={{ WebkitOverflowScrolling: "touch" }}>
        {chargement ? (
          <div className="mt-24 text-center text-[15px] text-black/35">Chargement du nuage…</div>
        ) : visibles.length === 0 ? (
          <div className="mt-20 rounded-3xl border border-dashed border-black/10 bg-white/70 px-5 py-8 text-center">
            <div className="text-[17px] font-semibold">Aucune bulle ici</div>
            <p className="mt-2 text-[14px] leading-relaxed text-black/45">
              Choisis un projet, une catégorie, puis lance une demande. Elle passera du blanc à l’orange, puis au vert.
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap content-start items-start gap-2.5 pb-4">
            {visibles.map((t, index) => {
              const s = STATUTS[t.status] || STATUTS.lancement;
              const taille = index % 5 === 0 ? "min-h-[118px] w-[48%]" : index % 3 === 0 ? "min-h-[104px] w-[45%]" : "min-h-[92px] w-[47%]";
              return (
                <button
                  key={t.id}
                  onClick={() => setSelection(t)}
                  className={`${taille} rounded-[28px] border px-3.5 py-3 text-left shadow-sm shadow-black/[0.03] active:scale-[0.98] ${s.bubble}`}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                    <span className={`truncate text-[10px] font-semibold uppercase tracking-wide ${s.text}`}>
                      {CATEGORIES.find((c) => c.key === t.category)?.label || t.category}
                    </span>
                  </div>
                  <div className="line-clamp-3 text-[14px] font-semibold leading-snug">{t.title || t.prompt}</div>
                  <div className={`mt-2 truncate text-[11px] ${s.text}`}>{s.label} · {dateCourte(t.updated_at)}</div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <div
        className="shrink-0 border-t border-black/5 bg-[#faf9f7] px-3 pt-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)" }}
      >
        {erreur && <div className="mb-2 rounded-2xl bg-red-50 px-3 py-2 text-[13px] text-red-700">{erreur}</div>}
        <div className="rounded-[24px] border border-black/10 bg-white px-3 py-2">
          <textarea
            rows={2}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={projectId ? "Nouvelle demande agent…" : "Choisis d’abord un projet"}
            disabled={!projectId}
            className="max-h-28 w-full resize-none bg-transparent text-[16px] leading-[1.4] outline-none placeholder:text-black/30 disabled:opacity-40"
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            <select
              value={categorie === "toutes" ? "developpement_app" : categorie}
              onChange={(e) => setCategorie(e.target.value)}
              className="min-w-0 flex-1 rounded-full bg-black/[0.04] px-3 py-2 text-[13px] outline-none"
            >
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.long}</option>)}
            </select>
            <button
              onClick={creerTache}
              disabled={!prompt.trim() || !projectId || creation}
              className="rounded-full bg-[#1a1a1a] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-25"
            >
              {creation ? "Lance…" : "Lancer"}
            </button>
          </div>
        </div>
      </div>

      {selection && (
        <>
          <div className="fixed inset-0 z-40 bg-black/25" onClick={() => setSelection(null)} />
          <div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[78dvh] overflow-y-auto rounded-t-3xl bg-white px-4 pt-2"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)", WebkitOverflowScrolling: "touch" }}
          >
            <div className="mx-auto mb-3 mt-1 h-1 w-9 rounded-full bg-black/15" />
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${(STATUTS[selection.status] || STATUTS.lancement).dot}`} />
                  <span className={`text-[12px] font-semibold ${(STATUTS[selection.status] || STATUTS.lancement).text}`}>
                    {(STATUTS[selection.status] || STATUTS.lancement).label}
                  </span>
                </div>
                <h2 className="text-[20px] font-semibold leading-tight">{selection.title}</h2>
                <p className="mt-1 text-[13px] text-black/40">
                  {selection.project_name} · {CATEGORIES.find((c) => c.key === selection.category)?.long || selection.category}
                </p>
              </div>
              <button onClick={() => setSelection(null)} className="rounded-full bg-black/[0.04] px-3 py-1.5 text-[13px]">Fermer</button>
            </div>

            <div className="mb-3 rounded-2xl bg-black/[0.035] px-3 py-3">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-black/35">Demande</div>
              <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{selection.prompt}</p>
            </div>

            <div className="mb-3 rounded-2xl bg-black/[0.035] px-3 py-3">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-black/35">État</div>
              <p className="text-[14px] text-black/55">{selection.last_event || "—"}</p>
              <p className="mt-1 text-[12px] text-black/35">Créée {dateCourte(selection.created_at)} · MAJ {dateCourte(selection.updated_at)}</p>
            </div>

            {selection.result && (
              <div className="mb-3 rounded-2xl bg-emerald-50 px-3 py-3">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Retour agent</div>
                <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-emerald-950">{selection.result}</p>
              </div>
            )}

            {selection.error && (
              <div className="mb-3 rounded-2xl bg-red-50 px-3 py-3">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-red-700">Blocage</div>
                <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-red-950">{selection.error}</p>
              </div>
            )}

            <button
              onClick={() => navigate(`/m/projet/${selection.project_id}`)}
              className="mb-1 w-full rounded-2xl bg-[#1a1a1a] px-4 py-3 text-[15px] font-semibold text-white"
            >
              Ouvrir le chat du projet
            </button>
          </div>
        </>
      )}
    </div>
  );
}
